"use client";

import { isBefore, isAfter, differenceInMonths } from "date-fns";
import { usePricelist } from "@/pricelist/PricelistContext";
import {
  BookItem,
  DigitalServiceItem,
  ProductBundleItem,
  SubscriptionItem,
} from "../types";
import { getPriceOffer } from "@/products/pricing";
import { Nullable } from "@/types";
import { ProductListSection } from "@/cms/sanity.types";
import { formatPublicationDate } from "@/utils/formatDate";
import { IndexDocument } from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { Tag } from "@cappelendamm/design-system/react";
import type { ItemListingTag as ListingTag } from "./types";

function isRecent(publicationDate: string | null): boolean {
  if (!publicationDate) return false;

  const pubDate = new Date(publicationDate);
  const now = new Date();

  // Recent if it's in the past AND within the last 3 months
  return isBefore(pubDate, now) && differenceInMonths(now, pubDate) <= 3;
}

function isUpcoming(publicationDate: string | null): boolean {
  if (!publicationDate) return false;

  const pubDate = new Date(publicationDate);
  const now = new Date();

  return isAfter(pubDate, now);
}

function getTagsFromTopics(
  topics: IndexDocument["etiketter"],
  type: "informational" | "highlighted",
): ListingTag[] {
  if (!topics) return [];

  return Object.values(topics)
    .flat()
    .filter((value) => value.includes(`/${type}/`))
    .map((value) => value.split("/").at(-1)!)
    .filter(Boolean)
    .map((label) => ({ label, type }));
}

/**
 * Shows a small label on a product card (like "Nyhet" or "Tilbud").
 * Only one label is shown at a time.
 * "Tilbud" only shows up if the current user actually has access to that price.
 */
export function ListingTags({
  item,
  config,
}: {
  item: BookItem | SubscriptionItem | ProductBundleItem | DigitalServiceItem;
  config?: Nullable<
    Pick<
      NonNullable<ProductListSection["fields"]>,
      | "showHighlighted"
      | "showPromoted"
      | "showInformational"
      | "showPublicationDate"
    >
  >;
}) {
  const { applicablePricelists } = usePricelist();

  const tags: ListingTag[] = [];

  const showHighlighted = config?.showHighlighted ?? true;
  const showInformational = config?.showInformational ?? true;
  const showPromoted = config?.showPromoted ?? true;
  const showPublicationDate = config?.showPublicationDate ?? false;
  const safePricelists = Array.isArray(item.priceLists) ? item.priceLists : [];

  // Optional "I salg ..." label based on formatted publication date.
  if (showPublicationDate && item.publicationDate) {
    const formattedPublicationDate = formatPublicationDate(
      item.publicationDate,
    );
    if (formattedPublicationDate) {
      tags.push({
        label: `I salg ${formattedPublicationDate}`,
        type: "informational",
      });
    }
  }

  // Informational tags: time-based labels first, then topic-driven labels.
  if (showInformational) {
    if (isUpcoming(item.publicationDate)) {
      tags.push({ label: "Kommer snart", type: "informational" });
    } else if (isRecent(item.publicationDate)) {
      tags.push({ label: "Nyhet", type: "informational" });
    }

    tags.push(...getTagsFromTopics(item.etiketter, "informational"));
  }

  // Promotion tags use the price offer computed from applicable pricelists only.
  if (showPromoted) {
    const offer = getPriceOffer({
      originalItemPrice: item.price,
      subjectToVAT: item.subjectToVAT,
      priceLists: safePricelists.filter((pricelist) =>
        applicablePricelists?.some(
          (applicable) => applicable.node?.identifier === pricelist.identifier,
        ),
      ),
    });

    if (offer.current === 0) {
      tags.push({ label: "Gratis", type: "promoted" });
    } else if (offer.hasOffer) {
      tags.push({ label: "Tilbud", type: "promoted" });
    }
  }

  // Highlighted tags are added last so they do not override promo/info/date tags.
  if (showHighlighted) {
    tags.push(...getTagsFromTopics(item.etiketter, "highlighted"));
  }

  // UI currently shows one tag at most.
  const [first] = tags;
  if (!first) return null;

  return (
    <div className="absolute top-0 left-0 flex flex-col gap-2 [&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:rounded-tr-md">
      <Tag variant={first.type}>{first.label}</Tag>
    </div>
  );
}
